import type { AnimationOptions, BreakpointRange } from '@wix/thunderbolt-becky-types'
import type { AnimationManager, EffectDataExtras } from 'feature-motion'
import type { CanvasInputSetters, CanvasNamedEffect, CanvasScene, IWixMediaCanvas } from '@wix/canvas'
import type { CanvasEffectData, CanvasPageConfig, CanvasVariation, CustomAnimation } from './types'
import { BrowserWindow } from '@wix/thunderbolt-symbols'

import {
	getCanvasScene,
	CANVAS_EFFECT_READY_EVENT,
	CANVAS_EFFECT_PLAY_EVENT,
	CANVAS_EFFECT_STOP_EVENT,
	CANVAS_SOURCE_UPDATE_EVENT,
	CANVAS_TARGET_RESIZE_EVENT,
} from '@wix/canvas'

const getMatchMediaString = (range: Pick<BreakpointRange, 'min' | 'max'>): string => {
	const mediaString = [] as Array<string>

	if (range.max) {
		mediaString.push(`(max-width:${range!.max}px)`)
	}
	if (range.min) {
		mediaString.push(`(min-width:${range!.min}px)`)
	}

	return mediaString.join(' and ')
}

export class CanvasManager {
	private compIdToEffectsIds: Record<string, Array<string>>
	private effectIdToCanvasVariations: Record<string, Array<CanvasVariation>>
	private effectIdToCustomAnimations: Record<string, { [animationId: string]: CustomAnimation }>
	private effectIdToActiveVariation: Record<string, CanvasVariation>
	private effectIdToCanvasScene: Record<string, CanvasScene<CanvasNamedEffect['type']>>
	private progressCallbacks: Record<string, () => any>
	private effectIdToMediaCanvas: Record<string, IWixMediaCanvas>
	private activeMediaListeners: Array<MediaQueryList>
	private currrentlyPlayingEffects: Set<string>
	private browserWindow: NonNullable<BrowserWindow>
	private viewportWidth: number
	private canvasReadyCallback: (e: CustomEvent) => void
	private canvasPlayCallback: (e: CustomEvent) => void
	private canvasStopCallback: (e: CustomEvent) => void
	private sourceUpdateCallback: (e: CustomEvent) => void
	private targetResizeCallback: (e: CustomEvent) => void
	private handleMediaQueryChange: (e: MediaQueryListEvent) => void

	constructor(browserWindow: NonNullable<BrowserWindow>) {
		this.compIdToEffectsIds = {}
		this.effectIdToCanvasVariations = {}
		this.effectIdToCustomAnimations = {}
		this.effectIdToActiveVariation = {}
		this.effectIdToCanvasScene = {}
		this.progressCallbacks = {}
		this.effectIdToMediaCanvas = {}
		this.activeMediaListeners = []
		this.currrentlyPlayingEffects = new Set<string>()

		this.browserWindow = browserWindow
		this.viewportWidth = browserWindow.innerWidth

		this.canvasReadyCallback = this._canvasReadyCallback.bind(this)
		this.canvasPlayCallback = this._canvasPlayCallback.bind(this)
		this.canvasStopCallback = this._canvasStopCallback.bind(this)
		this.sourceUpdateCallback = this._sourceUpdateCallback.bind(this)
		this.targetResizeCallback = this._targetResizeCallback.bind(this)
		this.handleMediaQueryChange = this._handleMediaQueryChange.bind(this)
	}

	init(
		motionManager: AnimationManager,
		animationDataByCompId: CanvasPageConfig['animationDataByCompId'],
		scrubTriggers: CanvasPageConfig['scrubTriggers'],
		canvasBpRanges: CanvasPageConfig['canvasBpRanges']
	) {
		this.compIdToEffectsIds = Object.keys(animationDataByCompId).reduce((acc, compId) => {
			acc[compId] = Object.keys(animationDataByCompId[compId])
			return acc
		}, {} as Record<string, Array<string>>)

		const animationData: Record<string, CanvasEffectData> = Object.assign(
			{},
			...Object.values(animationDataByCompId || {})
		)

		this.effectIdToCanvasVariations = Object.entries(animationData).reduce(
			(acc, [canvasEffectId, canvasAnimationData]) => {
				acc[canvasEffectId] = canvasAnimationData.canvasVariations
				return acc
			},
			{} as Record<string, Array<CanvasVariation>>
		)

		this.effectIdToCustomAnimations = Object.entries(animationData).reduce(
			(acc, [canvasEffectId, canvasAnimationData]) => {
				acc[canvasEffectId] = canvasAnimationData.customAnimations
				return acc
			},
			{} as Record<string, { [animationId: string]: CustomAnimation }>
		)

		const dataForMotion = Object.entries(this.effectIdToCustomAnimations).reduce(
			(acc, [canvasEffectId, customAnimations]) => {
				Object.entries(customAnimations).forEach(([animationId, customAnimation]) => {
					acc[animationId] = customAnimation.map((customAnimationVariation) => {
						const registerCustomEffect = (getProgress: () => number) => {
							this.progressCallbacks[animationId] = getProgress

							if (this.effectIdToCanvasScene[canvasEffectId]) {
								customAnimationVariation.customEffect?.ranges.forEach(({ name, ...rest }) => {
									this.effectIdToCanvasScene[canvasEffectId].addScrubEffect(
										{
											name: name as keyof CanvasInputSetters<CanvasNamedEffect['type']>,
											...rest,
										},
										getProgress
									)
								})
							}
						}

						return {
							...customAnimationVariation,
							registerCustomEffect,
						}
					})
				})
				return acc
			},
			{} as Record<
				string,
				Array<AnimationOptions & EffectDataExtras & { targetId: string; variants: Array<BreakpointRange> }>
			>
		)

		const isHoverSupported = this.browserWindow.matchMedia('(hover: hover)').matches

		motionManager.addExternalAnimationData(dataForMotion, scrubTriggers, isHoverSupported)

		this._observeBreakpointChange(canvasBpRanges)

		document.addEventListener(CANVAS_EFFECT_READY_EVENT, this.canvasReadyCallback as (e: Event) => void)
		document.addEventListener(CANVAS_EFFECT_PLAY_EVENT, this.canvasPlayCallback as (e: Event) => void)
		document.addEventListener(CANVAS_EFFECT_STOP_EVENT, this.canvasStopCallback as (e: Event) => void)
		document.addEventListener(CANVAS_SOURCE_UPDATE_EVENT, this.sourceUpdateCallback as (e: Event) => void)
		document.addEventListener(CANVAS_TARGET_RESIZE_EVENT, this.targetResizeCallback as (e: Event) => void)
	}

	destroy() {
		document.removeEventListener(CANVAS_EFFECT_READY_EVENT, this.canvasReadyCallback as (e: Event) => void)
		document.removeEventListener(CANVAS_EFFECT_PLAY_EVENT, this.canvasPlayCallback as (e: Event) => void)
		document.removeEventListener(CANVAS_EFFECT_STOP_EVENT, this.canvasStopCallback as (e: Event) => void)
		document.removeEventListener(CANVAS_SOURCE_UPDATE_EVENT, this.sourceUpdateCallback as (e: Event) => void)
		document.removeEventListener(CANVAS_TARGET_RESIZE_EVENT, this.targetResizeCallback as (e: Event) => void)

		this.activeMediaListeners.forEach((mediaQueryList) => {
			mediaQueryList.removeEventListener('change', this.handleMediaQueryChange)
		})
		this.activeMediaListeners.length = 0

		Object.keys(this.effectIdToCanvasScene).forEach((effectId) => {
			this._deleteScene(effectId)
		})
	}

	_updateScene(effectId: string, source: HTMLImageElement | HTMLVideoElement | null, target: HTMLCanvasElement) {
		const canvasEffectVariation = this.effectIdToActiveVariation[effectId]

		this.effectIdToCanvasScene[effectId]?.cancel()

		const canvasScene = getCanvasScene(source, target, canvasEffectVariation)

		if (!canvasScene) {
			delete this.effectIdToCanvasScene[effectId]
			return
		}

		this.effectIdToCanvasScene[effectId] = canvasScene

		Object.entries(this.effectIdToCustomAnimations[effectId]).forEach(([animationId, customAnimation]) => {
			if (this.progressCallbacks[animationId]) {
				customAnimation.forEach((customAnimationVariation) => {
					customAnimationVariation.customEffect?.ranges.forEach(({ name, ...rest }) => {
						canvasScene.addScrubEffect(
							{ name: name as keyof CanvasInputSetters<CanvasNamedEffect['type']>, ...rest },
							this.progressCallbacks[animationId]
						)
					})
				})
			}
		})
	}

	_play(effectId: string, reveal?: boolean) {
		this.effectIdToCanvasScene[effectId]?.play({ reveal })
		this.currrentlyPlayingEffects.add(effectId)
	}

	_getCurrentVariation(effectId: string) {
		const variations = this.effectIdToCanvasVariations[effectId]

		if (!variations) {
			return
		}

		const defaultVariation = variations.find((variation) => !(variation.variants && variation.variants.length))

		// @ts-expect-error
		return (variations.findLast((variation: CanvasVariation) => {
			return variation.variants?.some((variant: BreakpointRange) => {
				if (variant.max && variant.max < this.viewportWidth) {
					return false
				}

				if (variant.min && variant.min > this.viewportWidth) {
					return false
				}

				return true
			})
		}) || defaultVariation) as CanvasVariation
	}

	_updateEffectMediaCanvas(effectId: string) {
		const currentVariation = this._getCurrentVariation(effectId)
		const previousVariation = this.effectIdToActiveVariation[effectId]

		// TODO: this whole flow assumes a single effect per component since we have only a single attribute to update
		if (currentVariation) {
			if (currentVariation === previousVariation) {
				return
			}

			const mediaCanvasElement = this.effectIdToMediaCanvas[effectId]
			this.effectIdToActiveVariation[effectId] = currentVariation

			if (previousVariation) {
				const { source, target } = mediaCanvasElement

				if (target) {
					this._updateScene(effectId, source, target)

					if (this.currrentlyPlayingEffects.has(effectId)) {
						this._play(effectId)
					}
				}
			} else {
				if (currentVariation.namedEffect) {
					mediaCanvasElement.dataset.hasAnimation = 'true'
				} else {
					delete mediaCanvasElement.dataset.hasAnimation
				}
			}
		} else if (previousVariation) {
			this._deleteScene(effectId)
			delete this.effectIdToActiveVariation[effectId]
		}
	}

	_canvasReadyCallback(e: CustomEvent) {
		const mediaCanvasElement = e.target as IWixMediaCanvas
		const effectIds = this._getEffectIdsByMediaCanvas(mediaCanvasElement)

		if (effectIds) {
			effectIds.forEach((effectId) => {
				this.effectIdToMediaCanvas[effectId] = mediaCanvasElement

				this._updateEffectMediaCanvas(effectId)
			})
		}
	}

	_canvasPlayCallback(e: CustomEvent) {
		const mediaCanvasElement = e.target as IWixMediaCanvas
		const reveal = e.detail.reveal
		const effectIds = this._getEffectIdsByMediaCanvas(mediaCanvasElement)

		if (!effectIds) {
			return
		}

		const { source, target } = mediaCanvasElement

		effectIds.forEach((effectId) => {
			if (!this.effectIdToCanvasScene[effectId] && target) {
				this._updateScene(effectId, source, target)
			}

			this._play(effectId, reveal)
		})
	}

	_canvasStopCallback(e: CustomEvent) {
		const effectIds = this._getEffectIdsByMediaCanvas(e.target as IWixMediaCanvas) || []

		effectIds.forEach((effectId) => {
			if (this.effectIdToCanvasScene[effectId]) {
				this.effectIdToCanvasScene[effectId].pause()
				this.currrentlyPlayingEffects.delete(effectId)
			}
		})
	}

	_sourceUpdateCallback(e: CustomEvent) {
		const mediaCanvasElement = e.target as IWixMediaCanvas
		const effectIds = this._getEffectIdsByMediaCanvas(mediaCanvasElement) || []

		effectIds.forEach((effectId) => {
			if (this.effectIdToCanvasScene[effectId]) {
				this.effectIdToCanvasScene[effectId].sequence?.instance.setSource(mediaCanvasElement.source)
				if (mediaCanvasElement.target) {
					const { width, height } = mediaCanvasElement.target
					this.effectIdToCanvasScene[effectId].resize(width, height)
				}
			}
		})
	}

	_targetResizeCallback(e: CustomEvent) {
		const mediaCanvasElement = e.target as IWixMediaCanvas
		const effectIds = this._getEffectIdsByMediaCanvas(mediaCanvasElement) || []
		const { width, height } = e.detail

		effectIds.forEach((effectId) => {
			if (this.effectIdToCanvasScene[effectId]) {
				this.effectIdToCanvasScene[effectId].resize(width, height)
			}
		})
	}

	_deleteScene(effectId: string) {
		this.currrentlyPlayingEffects.delete(effectId)
		this.effectIdToCanvasScene[effectId]?.cancel()
		delete this.effectIdToMediaCanvas[effectId]?.dataset.hasAnimation
		delete this.effectIdToCanvasScene[effectId]
		// TODO: do we need to cancel all animations in this.effectIdToCustomAnimations[effectId]
	}

	_observeBreakpointChange(canvasBpRanges: CanvasPageConfig['canvasBpRanges']) {
		canvasBpRanges.forEach((range) => {
			const matchMediaString = getMatchMediaString(range)
			const mediaQueryList = this.browserWindow.matchMedia(matchMediaString)
			this.activeMediaListeners.push(mediaQueryList)
			mediaQueryList.addEventListener('change', this.handleMediaQueryChange)
		})
	}

	_handleMediaQueryChange(event: MediaQueryListEvent) {
		if (event.matches) {
			this.viewportWidth = this.browserWindow.innerWidth

			Object.keys(this.effectIdToMediaCanvas).forEach((effectId) => {
				this._updateEffectMediaCanvas(effectId)
			})
		}
	}

	_getEffectIdsByMediaCanvas(mediaCanvasElement: IWixMediaCanvas) {
		const containerId = mediaCanvasElement.dataset.containerId as string
		return this.compIdToEffectsIds[containerId]
	}
}
